import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { analyzeSpendingPatterns, generateDetailedPersona } from "../utils/guiltAlgorithm";
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ShareStatsCard from "./ShareStatsCard";

// Register required Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const ShoppingInsights = ({ purchases, theme }) => {
  const [insights, setInsights] = useState(null);
  const [persona, setPersona] = useState(null);
  const [activeTab, setActiveTab] = useState("patterns");
  
  useEffect(() => {
    if (purchases && purchases.length >= 5) {
      setInsights(analyzeSpendingPatterns(purchases));
      setPersona(generateDetailedPersona(purchases));
    }
  }, [purchases]);
  
  // If not enough purchases, show placeholder
  if (!purchases || purchases.length < 5) {
    return (
      <motion.div
        className="bg-white rounded-3xl shadow-xl p-6 mb-8 text-center"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="text-5xl mb-4">🔮</div>
        <h2 className="text-xl font-bold mb-2">Shopping Insights Coming Soon</h2>
        <p className="text-gray-600 mb-4">
          Log at least 5 purchases to unlock your personalized shopping insights and spending patterns.
        </p>
        <div className="bg-gray-50 rounded-xl p-4 text-left">
          <div className="font-semibold mb-2">What you'll discover:</div>
          <ul className="text-sm text-gray-600 space-y-2">
            <li>• Your unique shopping personality</li>
            <li>• Spending triggers and patterns</li>
            <li>• Personalized financial advice</li>
            <li>• Guilt trends over time</li>
          </ul>
        </div>
      </motion.div>
    );
  }
  
  // If we have insights but they're still loading
  if (!insights || !persona) {
    return (
      <motion.div
        className="bg-white rounded-3xl shadow-xl p-6 mb-8 text-center"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="text-4xl mb-4 animate-spin">🔄</div>
        <h2 className="text-xl font-bold">Analyzing your shopping habits...</h2>
      </motion.div>
    );
  }
  
  // Theme-based styling
  const tabStyles = {
    mint: "bg-emerald-500 text-white",
    pink: "bg-pink-500 text-white",
    lavender: "bg-purple-500 text-white"
  };
  
  // Prepare chart data for mood distribution
  const moodCounts = purchases.reduce((acc, p) => {
    acc[p.mood] = (acc[p.mood] || 0) + 1;
    return acc;
  }, {});
  
  const moodLabels = Object.keys(moodCounts);
  const moodData = Object.values(moodCounts);
  
  const chartColors = {
    backgroundColor: [
      'rgba(54, 162, 235, 0.6)',
      'rgba(255, 99, 132, 0.6)',
      'rgba(255, 206, 86, 0.6)',
      'rgba(75, 192, 192, 0.6)',
      'rgba(153, 102, 255, 0.6)',
      'rgba(255, 159, 64, 0.6)',
      'rgba(199, 199, 199, 0.6)',
    ],
    borderColor: [
      'rgba(54, 162, 235, 1)',
      'rgba(255, 99, 132, 1)',
      'rgba(255, 206, 86, 1)',
      'rgba(75, 192, 192, 1)',
      'rgba(153, 102, 255, 1)',
      'rgba(255, 159, 64, 1)',
      'rgba(199, 199, 199, 1)',
    ]
  };
  
  const chartData = {
    labels: moodLabels,
    datasets: [
      {
        data: moodData,
        backgroundColor: chartColors.backgroundColor.slice(0, moodLabels.length),
        borderColor: chartColors.borderColor.slice(0, moodLabels.length),
        borderWidth: 1,
      },
    ],
  };
  
  const chartOptions = {
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 15,
          padding: 10,
          font: {
            size: 11
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = Math.round((value / total) * 100);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
    maintainAspectRatio: false
  };
  
  return (
    <motion.div
      className="bg-white rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <h2 className="text-2xl font-bold mb-6">Shopping Insights</h2>
      
      {/* Shopping Persona Summary */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-600">Your Shopping Personality</div>
            <div className="text-xl font-bold">{persona.type}</div>
          </div>
          <div className="text-4xl">✨</div>
        </div>
        <p className="text-sm mt-2">{persona.description}</p>
      </div>
      
      {/* Tab Navigation */}
      <div className="flex mb-4 border-b">
        <button
          onClick={() => setActiveTab("patterns")}
          className={`px-4 py-2 font-medium text-sm rounded-t-lg ${
            activeTab === "patterns" ? tabStyles[theme] : "text-gray-600"
          }`}
        >
          Spending Patterns
        </button>
        <button
          onClick={() => setActiveTab("personality")}
          className={`px-4 py-2 font-medium text-sm rounded-t-lg ${
            activeTab === "personality" ? tabStyles[theme] : "text-gray-600"
          }`}
        >
          Your Personality
        </button>
        <button
          onClick={() => setActiveTab("tips")}
          className={`px-4 py-2 font-medium text-sm rounded-t-lg ${
            activeTab === "tips" ? tabStyles[theme] : "text-gray-600"
          }`}
        >
          Smart Tips
        </button>
      </div>
      
      {/* Tab Content */}
      <div className="mb-4">
        {/* Spending Patterns Tab */}
        {activeTab === "patterns" && (
          <div className="space-y-4">
            {/* Mood Distribution Chart */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold mb-3">What triggers your shopping?</h3>
              <div className="h-56">
                <Doughnut data={chartData} options={chartOptions} />
              </div>
              <div className="text-xs text-gray-500 italic text-center mt-2">
                Your most common shopping mood: <span className="font-semibold capitalize">{insights.topMood}</span>
              </div>
            </div>
            
            {/* Key Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-sm text-gray-500">Monthly Average</div>
                <div className="text-xl font-bold">${insights.monthlyAverage.toFixed(2)}</div>
              </div>
                              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-sm text-gray-500">Impulse Buys</div>
                <div className="text-xl font-bold">{insights.impulsePercentage.toFixed(0)}%</div>
              </div>
            </div>
            
            {/* Spending Trend */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold mb-2">Your Spending Trend</h3>
              <div className="flex items-center">
                <div className={`text-2xl mr-3 ${
                  insights.trend === "improving" ? "text-green-500" : "text-red-500"
                }`}>
                  {insights.trend === "improving" ? "📉" : "📈"}
                </div>
                <div>
                  <div className="font-medium">
                    {insights.trend === "improving" 
                      ? "Your guilt scores are decreasing" 
                      : "Your guilt scores are increasing"}
                  </div>
                  <div className="text-sm text-gray-600">
                    Based on your last {Math.min(5, purchases.length)} purchases
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Shopping Personality Tab */}
        {activeTab === "personality" && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold flex items-center mb-3">
                <span className="text-xl mr-2">🧠</span>
                {persona.type}
              </h3>
              <p className="text-gray-600 mb-3">{persona.description}</p>
              
              <h4 className="font-medium text-sm mb-2">Your Shopping Traits</h4>
              <ul className="space-y-1 text-sm mb-4">
                {persona.traits.map((trait, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-sm mr-2">•</span> {trait}
                  </li>
                ))}
              </ul>
              
              <div className="text-xs text-gray-500 italic">
                Based on the analysis of {insights.purchaseCount} purchases
              </div>
            </div>
            
            {/* Shopping Triggers */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold flex items-center mb-3">
                <span className="text-xl mr-2">🎯</span>
                Your Shopping Triggers
              </h3>
              
              <div className="space-y-3">
                <div>
                  <div className="text-sm font-medium mb-1">Primary Mood: <span className="capitalize">{insights.topMood}</span></div>
                  <div className="text-xs text-gray-600">
                    You tend to shop most when you're feeling this way
                  </div>
                </div>
                
                <div>
                  <div className="text-sm font-medium mb-1">Guilt Level: {insights.topGuiltLevel}</div>
                  <div className="text-xs text-gray-600">
                    Your most common guilt range after purchases
                  </div>
                </div>
                
                <div>
                  <div className="text-sm font-medium mb-1">Impulse Rate: {insights.impulsePercentage.toFixed(0)}%</div>
                  <div className="text-xs text-gray-600">
                    Percentage of purchases made on impulse
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Smart Tips Tab */}
        {activeTab === "tips" && (
          <div className="space-y-4">
            <div className="bg-blue-50 rounded-xl p-4">
              <h3 className="font-semibold flex items-center mb-3">
                <span className="text-xl mr-2">💡</span>
                Personalized Shopping Tips
              </h3>
              
              <div className="space-y-3">
                {persona.tips.map((tip, index) => (
                  <div key={index} className="flex items-start">
                    <div className="bg-white text-blue-500 rounded-full h-5 w-5 flex items-center justify-center text-xs font-bold mr-2 mt-0.5">
                      {index + 1}
                    </div>
                    <div className="text-sm text-gray-700">{tip}</div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Financial Impact */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold flex items-center mb-3">
                <span className="text-xl mr-2">💰</span>
                Financial Impact
              </h3>
              
              <div>
                <div className="font-medium mb-1">Total Spent: ${insights.totalSpent.toFixed(2)}</div>
                <div className="text-sm text-gray-600 mb-3">
                  Across {insights.purchaseCount} tracked purchases
                </div>
                
                <div className="text-sm mb-3">
                  <span className="font-medium">What if:</span> You invested half of your impulse purchases?
                </div>
                
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="font-bold text-lg mb-1">
                    ${(insights.totalSpent * (insights.impulsePercentage / 100) * 0.5).toFixed(2)}
                  </div>
                  <div className="text-xs text-gray-600">
                    At 7% annual return, this could grow to ${(insights.totalSpent * (insights.impulsePercentage / 100) * 0.5 * 1.4).toFixed(2)} in 5 years.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

        {/* Sharing Section - ADD THIS CODE RIGHT HERE */}
        {insights && persona && (
          <div className="mt-6 mb-4">
            <ShareStatsCard 
              purchaseData={{
                item: "insights", // Not used directly, helpful for identification
                amount: insights.totalSpent.toFixed(2),
                mood: insights.topMood || "shopping",
                usageFrequency: "regularly",
                guiltScore: Math.round(insights.avgGuilt) || 50,
                purchaseCount: insights.purchaseCount, // Include purchase count
                impulsePercentage: Math.round(insights.impulsePercentage) // Additional insights data
              }} 
              theme={theme} 
              userPersonality={persona.type}
              cardType="dashboard" // Use dashboard type for insights stats too
            />
          </div>
        )}
      
      <div className="text-center text-xs text-gray-500 italic">
        These insights are based on your purchase history and stored locally on your device only.
      </div>
    </motion.div>
  );
};

export default ShoppingInsights;
